/**
 * File names for operation files: the fixed names Architect and the worker give each artifact,
 * how a file name maps to a stage, group or preview kind, and the names and URLs used to
 * download files.
 */
export type OperationStage =
  "skirted-mesh" | "slice-file" | "path-file" | "cell-sim" | "run-settings";

export const OPERATION_STAGES: OperationStage[] = [
  "skirted-mesh",
  "slice-file",
  "path-file",
  "cell-sim",
  "run-settings",
];

export const STAGE_LABELS: Record<OperationStage, string> = {
  "skirted-mesh": "Skirted Mesh",
  "slice-file": "Slice File",
  "path-file": "Path File",
  "cell-sim": "Cell Sim",
  "run-settings": "Run Settings",
};

export const STAGE_REQUIRES: Partial<Record<OperationStage, OperationStage[]>> = {
  "path-file": ["slice-file", "skirted-mesh"],
  "slice-file": ["skirted-mesh"],
};

export const STAGE_REQUIRED_BY: Partial<Record<OperationStage, OperationStage[]>> = {
  "skirted-mesh": ["slice-file", "path-file"],
  "slice-file": ["path-file"],
};

export function classifyFileToStage(fileName: string): OperationStage | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".ply") || lower.endsWith(".stl")) return "skirted-mesh";
  if (lower.endsWith(".build.csv") || lower.endsWith(".slice.csv")) return "slice-file";
  if (lower.endsWith(".path.csv")) return "path-file";
  if (lower.endsWith(".json")) return "cell-sim";
  if (lower.endsWith(".yaml")) return "run-settings";
  return null;
}

export type FileGroup = "run" | "generated" | "image" | "temp" | "hidden";

/**
 * Canonical role names for the forming artifacts Architect ingests/generates. These are plain
 * values each producer applies to its own artifact at ingest (the mesh upload, the forming-yaml
 * generator) — there is intentionally no global filename→filename dispatcher that would have to
 * know every artifact type at once.
 */
export const SKIRTED_MESH_FILENAME = "skirted_mesh.stl";
export const FORMING_YAML_FILENAME = "forming.Form.yaml";

export const SCAN_PATH_FILENAME = "scanning.path.csv";
export const SCAN_CONFIG_FILENAME = "scanning.Scan.yaml";
export const CUT_PATH_FILENAME = "cutting.path.csv";
export const CUT_CONFIG_FILENAME = "cutting.Cut.yaml";

// Must match the names the worker derives from the skirted-mesh stem, so an imported file and a
// generated one are interchangeable.
const SKIRTED_MESH_STEM = SKIRTED_MESH_FILENAME.replace(/\.stl$/i, "");
export const SLICE_FILENAME = `${SKIRTED_MESH_STEM}.slice.csv`;
export const PATH_FILENAME = `${SKIRTED_MESH_STEM}.path.csv`;
export const PART_PLACEMENT_FILENAME = `${SKIRTED_MESH_STEM}.part-placement.json`;
// The full-resolution roboviz solve report, published from the Cell Sim session so later
// sessions can query keyframes without re-solving. Suffix matches roboviz's report naming
// (artifacts.upsampled_report).
export const SIM_REPORT_FILENAME = `${SKIRTED_MESH_STEM}.upsampled.report`;

// Rendering proxies: derived media an artifact is drawn from. Named off the same stem the worker
// derives them from, so an imported artifact and a generated one carry the same pair.
export const SLICE_PROXY_FILENAME = `${SKIRTED_MESH_STEM}.slice.glb`;
export const PATH_PROXY_FILENAME = `${SKIRTED_MESH_STEM}.path.glb`;

export function classifyFile(fileName: string): FileGroup {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".slice.glb") || lower.endsWith(".path.glb")) return "hidden";
  if (lower.endsWith(".build.csv") || lower.endsWith(".yaml")) return "run";
  if (
    lower.endsWith(".ply") ||
    lower.endsWith(".xyz") ||
    lower.endsWith(".slice.csv") ||
    lower.endsWith(".path.csv")
  )
    return "generated";
  if (
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".png") ||
    lower.endsWith(".gif") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".svg")
  )
    return "image";
  return "temp";
}

export type PreviewKind = "stl" | "glb" | "slice-csv" | "image" | "data";

export function classifyForPreview(fileName: string): PreviewKind | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".slice.csv") || lower.endsWith(".path.csv")) return "slice-csv";
  if (lower.endsWith(".stl")) return "stl";
  if (lower.endsWith(".glb")) return "glb";
  if (classifyFile(fileName) === "image") return "image";
  if (
    lower.endsWith(".csv") ||
    lower.endsWith(".yaml") ||
    lower.endsWith(".yml") ||
    lower.endsWith(".json") ||
    lower.endsWith(".txt") ||
    lower.endsWith(".log") ||
    lower.endsWith(".md")
  )
    return "data";
  return null;
}

const PART_NAME_MAX = 60;
const OPERATION_NAME_MAX = 40;
const PROCEDURE_NAME_MAX = 60;
const DOWNLOAD_NAME_MAX = 200;

function cutEnd(value: string, length: number): string {
  return value.length > length ? value.slice(0, length).replace(/[._]+$/, "") : value;
}

function cleanSegment(value: string, max = Infinity): string {
  const cleaned = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/_?\._?/g, ".")
    .replace(/^_|_$/g, "");
  return cutEnd(cleaned, max);
}

const WINDOWS_DEVICE_NAME = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;

/**
 * The procedure's segment is deliberately less lossy than cleanSegment, so do not unify them.
 * Procedure names are only unique as typed ("Trial 1" and "Trial #1" are two procedures), and
 * cleanSegment's ASCII-only output collapses both to "Trial_1" and an all-non-Latin name to
 * nothing. This replaces only what Windows or macOS refuse in a file name, so two procedures'
 * downloads stay apart. Part and operation names keep cleanSegment.
 */
export function procedureSegment(name: string): string {
  const legal = name
    .normalize("NFC")
    .replace(/[<>:"/\\|?*\u0000-\u001f\u007f]+/g, "_")
    .replace(/^[. ]+|[. ]+$/g, "");
  // Cut on code points, not UTF-16 units: a cut through an emoji leaves a lone surrogate, which
  // encodeURIComponent refuses, and every download under the procedure would fail.
  const codePoints = Array.from(legal);
  const cleaned = (
    codePoints.length > PROCEDURE_NAME_MAX
      ? codePoints
          .slice(0, PROCEDURE_NAME_MAX)
          .join("")
          .replace(/[._]+$/, "")
      : legal
  ).replace(/[. ]+$/, "");
  if (!cleaned.replace(/_/g, "")) return "procedure";
  return WINDOWS_DEVICE_NAME.test(cleaned) ? `${cleaned}_` : cleaned;
}

export function runTimeSegment(startedAt: string): string | undefined {
  const time = new Date(startedAt);
  if (Number.isNaN(time.getTime())) return undefined;
  return `${time.toISOString().slice(0, 19).replace(/:/g, "")}Z`;
}

export function downloadFileName({
  partName,
  procedureName,
  operationName,
  runStartedAt,
  fileName,
}: {
  partName?: string;
  procedureName?: string;
  operationName?: string;
  runStartedAt?: string;
  fileName: string;
}): string {
  const owners = [
    partName && cleanSegment(partName, PART_NAME_MAX),
    procedureName && procedureSegment(procedureName),
    operationName && cleanSegment(operationName, OPERATION_NAME_MAX),
    runStartedAt && runTimeSegment(runStartedAt),
  ]
    .filter(Boolean)
    .join("_");
  const cleaned = cleanSegment(fileName);
  const extension = cleaned.match(/\.[^.]{0,16}$/)?.[0] ?? "";
  const stem = cleaned.slice(0, cleaned.length - extension.length);
  if (!stem) return `${owners || "file"}${extension}`;
  const prefix = owners ? `${owners}_` : "";
  return `${prefix}${cutEnd(stem, DOWNLOAD_NAME_MAX - prefix.length - extension.length)}${extension}`;
}

const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/g;

/**
 * A Content-Disposition header for a download name that may hold any Unicode. Headers only carry
 * Latin-1, so the name goes in filename* (RFC 5987), and filename= carries an ASCII stand-in that
 * is never empty, first, for clients that do not read filename*.
 */
export function contentDisposition(name: string, { inline = false } = {}): string {
  const ascii = cleanSegment(name);
  const extension = ascii.match(/\.[^.]{0,16}$/)?.[0] ?? "";
  const stem = ascii.slice(0, ascii.length - extension.length);
  const encoded = encodeURIComponent(name.replace(LONE_SURROGATE, "\ufffd")).replace(
    /['()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
  );
  return `${inline ? "inline" : "attachment"}; filename="${stem || "file"}${extension}"; filename*=UTF-8''${encoded}`;
}

export function downloadSourceName({
  fileName,
  uploadedName,
  meshUploadedName,
}: {
  fileName: string;
  uploadedName?: string;
  meshUploadedName?: string;
}): string {
  if (uploadedName) return uploadedName;
  if (!meshUploadedName || !fileName.startsWith(`${SKIRTED_MESH_STEM}.`)) return fileName;
  const meshStem = meshUploadedName.replace(/\.[^.]*$/, "");
  return `${meshStem}${fileName.slice(SKIRTED_MESH_STEM.length)}`;
}

type DownloadTarget = {
  projectId: string;
  md5: string;
  attachmentId?: string | null;
  fileName: string;
  partId?: string;
  procedureId?: string;
  operationId?: string;
  inline?: boolean;
};

function setDefined(params: URLSearchParams, values: Record<string, string | null | undefined>) {
  for (const [key, value] of Object.entries(values)) {
    if (value) params.set(key, value);
  }
  return params;
}

export function downloadHref({ attachmentId, md5, inline, ...rest }: DownloadTarget): string {
  const params = setDefined(new URLSearchParams(attachmentId ? { attachmentId } : { md5 }), rest);
  if (inline) params.set("inline", "true");
  return `/api/download?${params}`;
}

export function runDownloadHref(target: {
  projectId: string;
  partId?: string;
  procedureId?: string;
  operationId: string;
  runUuid: string;
  cellId: string;
  association?: string;
}): string {
  return `/api/download/run?${setDefined(new URLSearchParams(), target)}`;
}
